// src/contexts/AdminAuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AdminAuthContext = createContext();

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth debe ser usado dentro de AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

// URL base de la API
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Verificar validez del token
const verifyToken = useCallback(async (tokenToVerify) => {
  try {
    const response = await fetch(`${API_URL}/api/admin/verify`, {
      method: 'GET',  // Cambiado de POST a GET
      headers: {
        'Authorization': `Bearer ${tokenToVerify}`,
        'Content-Type': 'application/json'
      }
    });

    const data = await response.json();

    if (data.success) {
      setToken(tokenToVerify);
      setAdmin(data.admin);
      setIsAuthenticated(true);
      localStorage.setItem('admin_token', tokenToVerify);
    } else {
      // Token inválido, limpiar storage
      localStorage.removeItem('admin_token');
      setToken(null);
      setAdmin(null);
      setIsAuthenticated(false);
    }
  } catch (error) {
    console.error('Error verificando token:', error);
    localStorage.removeItem('admin_token');
    setToken(null);
    setAdmin(null);
    setIsAuthenticated(false);
  } finally {
    setLoading(false);
  }
}, [API_URL]);

// Verificar token al cargar la aplicación
useEffect(() => {
  const storedToken = localStorage.getItem('admin_token');
  if (storedToken) {
    verifyToken(storedToken);
  } else {
    setLoading(false);
  }
}, [verifyToken]);

  // Función de login tradicional
  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (data.success) {
        setToken(data.token);
        setAdmin(data.admin);
        setIsAuthenticated(true);
        localStorage.setItem('admin_token', data.token);
        
        return { success: true, message: data.message };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('Error en login:', error);
      return { success: false, error: 'Error de conexión' };
    }
  };

  // Función de login con Google OAuth
  const googleLogin = async (idToken) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/login/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ idToken })
      });

      const data = await response.json();

      if (data.success) {
        setToken(data.token);
        setAdmin(data.admin);
        setIsAuthenticated(true);
        localStorage.setItem('admin_token', data.token);
        
        return { success: true, message: 'Login con Google exitoso' };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('Error en Google login:', error);
      return { success: false, error: 'Error de conexión con Google' };
    }
  };

  // Función de logout
  const logout = () => {
    setToken(null);
    setAdmin(null);
    setIsAuthenticated(false);
    localStorage.removeItem('admin_token');
  };

  // Función para hacer peticiones autenticadas
  const authenticatedFetch = async (url, options = {}) => {
    if (!token) {
      throw new Error('No hay token de autenticación');
    }

    const authOptions = {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    };

    const response = await fetch(`${API_URL}${url}`, authOptions);
    
    // Si recibimos 401 o 403, el token probablemente expiró
    if (response.status === 401 || response.status === 403) {
      logout();
      throw new Error('Sesión expirada');
    }

    return response;
  };

  const value = {
    isAuthenticated,
    admin,
    token,
    loading,
    login,
    googleLogin,  // Nueva función agregada
    logout,
    authenticatedFetch,
    verifyToken
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};